import { rd, RemoteData } from '@passionware/monads';
import {
  AbstractFilterWidget,
  AbstractFilterWidgetOperatorTrigger,
  AbstractFilterWidgetValueTrigger,
  FilterWidgetPropsBase
} from './_private/AbstractFilterWidget';
import {
  enumFilter,
  EnumFilter as EnumFilterType
} from 'v5/api/_common/query/filters/EnumFilter';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger
} from 'v5/design-system/Select';
import { Badge } from 'v5/design-system/Badge';
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxQueryRenderer,
  ComboboxSeparator,
  ComboboxTrigger
} from 'v5/design-system/Combobox';
import { Skeleton } from 'v5/design-system/Skeleton';

export interface EnumFilterDisplayValue<V> {
  label: string;
  value: V;
  icon?: ReactNode;
}

/** Options for enum filters; also used as {@link FilterEnumConfig} `options`. */
export type EnumFilterWidgetConfig<V, Q = void> = {
  useValues: (values: V[]) => RemoteData<EnumFilterDisplayValue<V>[]>;
  useOptions: (
    search: string,
    query: Q
  ) => RemoteData<EnumFilterDisplayValue<V>[]>;
  /**
   * Entity field shape. `'array'` enables “is all of” (AND). Defaults to `'scalar'`.
   */
  valueType?: 'scalar' | 'array';
};

export interface EnumFilterWidgetProps<V, Q = void>
  extends FilterWidgetPropsBase<EnumFilterType<V>> {
  config: EnumFilterWidgetConfig<V, Q>;
  filterField?: string;
  query: Q;
}

const EnumFilterWidgetInner = <V, Q = void>(
  {
    config,
    value,
    onValueChange,
    filterField,
    query,
    ...rest
  }: EnumFilterWidgetProps<V, Q>,
  ref: React.Ref<HTMLDivElement>
) => {
  const { t } = useTranslation('patterns');

  const valueOptions = config.useValues(value.value);

  const requiresValues = enumFilter.isSelectableOperator(value.operator);

  const handleOperatorChange = (next: string) => {
    const nextOperator = next as EnumFilterType<V>['operator'];
    onValueChange({
      ...value,
      operator: nextOperator,
      value: enumFilter.isSelectableOperator(nextOperator) ? value.value : []
    });
  };

  const operatorLabels: Record<EnumFilterType<V>['operator'], string> = {
    matchAny: t('EnumFilterWidget.operator.matchAny'),
    matchAll: t('EnumFilterWidget.operator.matchAll'),
    matchNone: t('EnumFilterWidget.operator.matchNone'),
    empty: t('EnumFilterWidget.operator.empty'),
    notEmpty: t('EnumFilterWidget.operator.notEmpty')
  };

  return (
    <AbstractFilterWidget
      ref={ref}
      filterField={filterField}
      {...rest}
      operatorSlot={
        <Select value={value.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger asChild>
            <AbstractFilterWidgetOperatorTrigger>
              {operatorLabels[value.operator]}
            </AbstractFilterWidgetOperatorTrigger>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="matchAny">{operatorLabels.matchAny}</SelectItem>
            {(config.valueType === 'array' ||
              value.operator === 'matchAll') && (
              <SelectItem value="matchAll">
                {operatorLabels.matchAll}
              </SelectItem>
            )}
            <SelectItem value="matchNone">
              {operatorLabels.matchNone}
            </SelectItem>
            <SelectItem value="empty">{operatorLabels.empty}</SelectItem>
            <SelectItem value="notEmpty">{operatorLabels.notEmpty}</SelectItem>
          </SelectContent>
        </Select>
      }
      valueSlot={
        requiresValues ? (
          <Combobox>
            <ComboboxTrigger asChild>
              <AbstractFilterWidgetValueTrigger data-element="value-trigger">
                <EnumFilterWidgetValue value={value.value} config={config} />
              </AbstractFilterWidgetValueTrigger>
            </ComboboxTrigger>
            <ComboboxContent className="max-h-[50vh]">
              <ComboboxInput
                placeholder={t('EnumFilterWidget.searchPlaceholder')}
              />
              <ComboboxList>
                <ComboboxQueryRenderer>
                  {search =>
                    rd.map(config.useOptions(search, query), options => {
                      return (
                        <>
                          {!search &&
                            rd.tryMap(valueOptions, valueOptions => (
                              <>
                                <ComboboxGroup>
                                  {valueOptions.map(option => (
                                    <ComboboxItem
                                      key={String(option.value)}
                                      value={String(option.value)}
                                      isSelected={value.value.some(current =>
                                        Object.is(current, option.value)
                                      )}
                                      closeOnSelect={false}
                                      onSelect={() => {
                                        const next = value.value.some(current =>
                                          Object.is(current, option.value)
                                        )
                                          ? value.value.filter(
                                              current =>
                                                !Object.is(
                                                  current,
                                                  option.value
                                                )
                                            )
                                          : [...value.value, option.value];
                                        onValueChange({
                                          ...value,
                                          value: next
                                        });
                                      }}
                                    >
                                      {option.icon}
                                      {option.label}
                                    </ComboboxItem>
                                  ))}
                                </ComboboxGroup>
                                <ComboboxSeparator />
                              </>
                            ))}
                          <ComboboxGroup>
                            {options
                              .filter(
                                option =>
                                  search ||
                                  !enumFilter.isIncluded(value, option.value)
                              )
                              .map(option => {
                                const isSelected = enumFilter.isIncluded(
                                  value,
                                  option.value
                                );
                                return (
                                  <ComboboxItem
                                    key={String(option.value)}
                                    value={String(option.value)}
                                    isSelected={isSelected}
                                    closeOnSelect={false}
                                    onSelect={() => {
                                      onValueChange(
                                        enumFilter.toggle(value, option.value)
                                      );
                                    }}
                                  >
                                    {option.icon}
                                    {option.label}
                                  </ComboboxItem>
                                );
                              })}
                          </ComboboxGroup>
                        </>
                      );
                    })
                  }
                </ComboboxQueryRenderer>
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        ) : null
      }
    />
  );
};

export const EnumFilterWidget = forwardRef(EnumFilterWidgetInner) as <
  V,
  Q = void
>(
  props: EnumFilterWidgetProps<V, Q> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof EnumFilterWidgetInner<V, Q>>;

export interface EnumFilterWidgetValueProps<V> {
  value: V[];
  config: {
    useValues: (values: V[]) => RemoteData<EnumFilterDisplayValue<V>[]>;
  };
}

export function EnumFilterWidgetValue<V>({
  value,
  config,
  ...rest
}: EnumFilterWidgetValueProps<V>) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'EnumFilterWidget'
  });
  const resolvedValues = config.useValues(value);
  if (!value.length) {
    return <span className="text-base text-fg-subtle">{t('emptyValue')}</span>;
  }
  return rd
    .journey(resolvedValues)
    .wait(() => (
      <>
        {value.map(_ => (
          <Skeleton key={String(_)} className="h-5 w-16 rounded" />
        ))}
      </>
    ))
    .catch(() => null)
    .map(items => (
      <>
        {items.map((item, idx) => (
          <Badge
            key={String(item.value)}
            variant="info"
            tone="subtle"
            size="sm"
            className="pointer-events-none"
          >
            {item.icon && (
              <span className="mr-1 inline-flex items-center justify-center">
                {item.icon}
              </span>
            )}
            {item.label}
          </Badge>
        ))}
      </>
    ));
}
